import { SupplierInquiry } from '@texas/api/endpoints/inquiryApi';
import { EntityType, trackerApi } from '@texas/api/endpoints/trackerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { inquiryEvents } from '@texas/components/article/variant/inquiry/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { trackerEvents } from '@texas/components/shared/tracker/events';
import { useEffect, useMemo } from 'react';
import { InquiriesOptions } from '../shared/types';

type InquiryData = HttpClientResponse<SupplierInquiry[]>;

type ArgsNullable = [pointer: number | null, entityType: EntityType | null];
type ArgsNonNullable = [pointer: number, entityType: EntityType];

function createInquiriesFetcher(
  usePointer: boolean,
): (...args: ArgsNullable | ArgsNonNullable) => Promise<InquiryData> {
  if (usePointer) {
    return ((...args: ArgsNonNullable) =>
      trackerApi.getInquiriesFromPointer(...args)) as (
      ...args: ArgsNullable | ArgsNonNullable
    ) => Promise<InquiryData>;
  } else {
    return ((...args: ArgsNullable) => trackerApi.getInquiries(...args)) as (
      ...args: ArgsNullable | ArgsNonNullable
    ) => Promise<InquiryData>;
  }
}

export function useInquiriesWidget({
  options,
  widgetId,
}: {
  options?: InquiriesOptions;
  widgetId: string;
}) {
  const fetcher = useMemo(
    () => createInquiriesFetcher(options?.trackerPointer !== undefined),
    [options?.trackerPointer],
  );

  const { data, refetch, loading, error } = useApiResource(fetcher);

  useEffect(() => {
    if (!options?.trackerPointer || !options.entityType) {
      refetch(null, null);
      return;
    }
    refetch(options.trackerPointer, options.entityType);
  }, [options?.entityType, options?.trackerPointer, refetch]);

  useEffect(() => {
    return inquiryEvents.onVariantUpdated.subscribe((variantId) => {
      if (
        options?.entityType === EntityType.Variant &&
        options.trackerPointer != null &&
        options.trackerPointer !== variantId
      )
        return;
      refetch(options?.trackerPointer ?? null, options?.entityType ?? null);
    });
  }, [options?.entityType, options?.trackerPointer, refetch]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(options?.trackerPointer ?? null, options?.entityType ?? null);
    },
    signalEvents: [
      inquiryEvents.onUpdated,
      trackerEvents.onAdded,
      trackerEvents.onRemoved,
    ],
  });

  return {
    data: data ?? [],
    loading,
    error,
    widgetId,
  };
}
